/**
 * La fiche d'un élève, composée une fois pour ses deux contenants hors live : poussée (`student-profil`
 * et les routes que ses onglets reprennent) et à droite de Mes élèves au large.
 *
 * Elle lit l'élève et son brief (`useTutorStudentProfileData`), fait le view-model de l'Aperçu, puis
 * demande à chaque onglet ses props et ses surfaces (`tutorStudentTabs.ts`). Un onglet sans props n'est
 * pas là. Elle s'ouvre toujours avec ses onglets : la seule entrée de l'ancienne fiche sans ses gestes
 * (`withActions` absent) était l'entretien hebdomadaire, un écran mort.
 *
 * L'onglet ouvert n'est pas à elle : l'écran le garde (il survit au changement d'élève au large).
 */
import React, { useMemo } from "react"
import { useSelector } from "react-redux"

import { type TutorStudentProfilePageProps } from "../../../../../../design-system/pages/TutorStudentProfilePage"
import { makeTutorStudentProfileViewModel } from "../../../../view-models/tutor/Students/tutorStudentsViewModel"
import { type UITutorStudentTab } from "../../../types/tutorEleves"
import { type OpenGeneration, type TutorStudentId, type TutorStudentTabArgs } from "./tutorStudentTabs"
import { type TutorStudentBriefParams, useTutorStudentProfileData } from "./useTutorStudentProfileData"
import { useTutorStudentActivitiesTab } from "./useTutorStudentActivitiesTab"
import { useTutorStudentAssessmentsTab } from "./useTutorStudentAssessmentsTab"
import { useTutorStudentMessagesTab } from "./useTutorStudentMessagesTab"
import { useTutorStudentProgramSheet } from "./useTutorStudentProgramSheet"
import { useTutorStudentReportsTab } from "./useTutorStudentReportsTab"

export type UseTutorStudentProfileArgs = {
    studentId: TutorStudentId | null
    tab: UITutorStudentTab
    onChangeTab: (tab: UITutorStudentTab) => void
    /** La fiche se voit. Cachée, ses onglets ne lisent ni ne marquent rien. */
    visible: boolean
    briefParams?: TutorStudentBriefParams
    /** Le retour ; absent au large (la fiche est un volet). */
    onBack?: () => void
    /** « Générer une activité », monté par l'écran ; absent : pas de bouton. */
    openGeneration?: OpenGeneration
}

const NO_GENERATION: OpenGeneration = () => undefined

export function useTutorStudentProfile(args: UseTutorStudentProfileArgs) {
    const { studentId, tab, onChangeTab, visible, briefParams, onBack, openGeneration } = args
    const data = useTutorStudentProfileData(visible ? studentId : null, briefParams)
    const viewModel = useMemo(makeTutorStudentProfileViewModel, [])
    const profile = useSelector((state: unknown) => viewModel(state, { studentId, raw: data.raw, brief: data.brief }))

    const tabArgs = (key: UITutorStudentTab): TutorStudentTabArgs => ({
        studentId,
        visible,
        active: visible && tab === key,
        openGeneration: openGeneration ?? NO_GENERATION,
    })
    const activities = useTutorStudentActivitiesTab(tabArgs("activities"))
    const reports = useTutorStudentReportsTab(tabArgs("reports"))
    const assessments = useTutorStudentAssessmentsTab(tabArgs("assessments"))
    // Une pièce de la conversation dont une activité est née ouvre sa fiche, montée par l'onglet Activités.
    const messages = useTutorStudentMessagesTab({
        ...tabArgs("messages"),
        student: profile?.student ?? null,
        onOpenActivity: activities.openActivity,
    })
    const program = useTutorStudentProgramSheet({
        studentId,
        hasStudyProgram: profile?.hasStudyProgram === true,
    })

    // Le compte de l'onglet Rapports : le total de la liste de CET élève, une fois lue (la liste du
    // serveur ne sert pas `live_reports` ; l'ancienne ligne n'en montrait donc pas).
    const reportsCount = reports.count
    const shownProfile = useMemo(
        () =>
            profile != null && reportsCount != null
                ? { ...profile, student: { ...profile.student, liveReportsCount: reportsCount } }
                : profile,
        [profile, reportsCount]
    )

    const page: TutorStudentProfilePageProps = {
        profile: shownProfile,
        tab,
        onChangeTab,
        onBack,
        onGenerate:
            openGeneration != null && studentId != null
                ? () => openGeneration({ kind: "student", studentId })
                : undefined,
        briefLoading: data.briefLoading,
        onOpenStudyProgram: program.open,
        activities: activities.props,
        reports: reports.props,
        assessments: assessments.props,
        messages: messages.props,
        // Une fiche pas encore lue : son squelette ; un échec sans élève à montrer : la panne, et « Réessayer ».
        loading: profile == null && data.studentError == null,
        error: profile == null ? data.studentError : null,
        onRetry: data.retryStudent,
    }

    const overlay = (
        <>
            {activities.overlay}
            {reports.overlay}
            {assessments.overlay}
            {messages.overlay}
            {program.overlay}
        </>
    )
    return { page, overlay }
}
